import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, FileText, Building2, Settings, Scale } from 'lucide-react';
import UserManagement from '../components/admin/UserManagement';
import OrderManagement from '../components/admin/OrderManagement';
import CustomerManagement from '../components/admin/CustomerManagement';
import RateListManagement from '../components/admin/RateListManagement';
import BSRateManagement from '../components/admin/BSRateManagement';
import TransportManagement from '../components/admin/TransportManagement';
import MasterTableManagement from '../components/admin/MasterTableManagement';
import DailyCapacitySettings from '../components/admin/DailyCapacitySettings';
import OpeningBalanceBSStock from '../components/admin/OpeningBalanceBSStock';

const PRODUCTION_STAGES = [
  'Base Shell Procurement', 'MS Grinding', 'Plating', 
  'Lathe', 'Polish', 'Planning', 'Engraving', 'Chroming', 'Proofing', 'Dispatch'
];

const MASTER_ITEMS = [
  { key: 'rate-list', label: 'Rate List' },
  { key: 'process-status', label: 'Process Status' },
  { key: 'design-status', label: 'Design Status' },
  { key: 'employee-details', label: 'Employee Details' },
  { key: 'colours', label: 'Colours' },
  { key: 'printing-substrates', label: 'Printing Substrate' },
  { key: 'lamination-substrates', label: 'Lamination Substrate' },
  { key: 'priorities', label: 'Priority' },
  { key: 'defects', label: 'Defects' },
  { key: 'bs-suppliers', label: 'BS Suppliers' },
  { key: 'credibility-ratings', label: 'Credibility Rating' },
  { key: 'parent-customers', label: 'Parent Customer' },
  { key: 'bs-rates', label: 'BS Rate' },
  { key: 'transports', label: 'Transport' },
  { key: 'daily-capacity', label: 'Daily Capacity' },
];

const AdminDashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'users');
  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state]);
  const [activeMaster, setActiveMaster] = useState('rate-list');
  const [showMasterDropdown, setShowMasterDropdown] = useState(false);
  const [activeOBSection, setActiveOBSection] = useState('bs-stock');

  const renderMasterContent = () => {
    switch (activeMaster) {
      case 'rate-list':
        return <RateListManagement />;
      case 'process-status':
        return (
          <MasterTableManagement title="Process Status" apiEndpoint="/process-status"
            fields={[
              { name: 'process_name', label: 'Process Name', type: 'text', required: true },
              { name: 'sequence', label: 'Sequence', type: 'number', required: true },
              { name: 'process_type', label: 'Process Type', type: 'select', required: true, options: [
                { value: 'BaseShell', label: 'BaseShell' }, { value: 'Design', label: 'Design' }
              ]}
            ]}
            displayField="process_name" secondaryField="process_type"
          />
        );
      case 'design-status':
        return (
          <MasterTableManagement title="Design Status" apiEndpoint="/design-status"
            fields={[
              { name: 'process_name', label: 'Process Name', type: 'text', required: true },
              { name: 'remark', label: 'Remark', type: 'text', required: false }
            ]}
            displayField="process_name" secondaryField="remark"
          />
        );
      case 'employee-details':
        return (
          <MasterTableManagement title="Employee Details" apiEndpoint="/employee-details"
            fields={[
              { name: 'name', label: 'Name', type: 'text', required: true },
              { name: 'address', label: 'Address', type: 'text', required: false },
              { name: 'mobile', label: 'Mobile', type: 'text', required: false },
              { name: 'esi_number', label: 'ESI Number', type: 'text', required: false },
              { name: 'pf_number', label: 'PF Number', type: 'text', required: false },
              { name: 'processes_performed', label: 'Processes Assigned', type: 'multiselect', required: false, options: PRODUCTION_STAGES }
            ]}
            displayField="name" secondaryField="mobile"
          />
        );
      case 'colours':
        return <MasterTableManagement title="Colour" apiEndpoint="/colours" fields={[{ name: 'colour', label: 'Colour Name', type: 'text', required: true }]} displayField="colour" />;
      case 'printing-substrates':
        return <MasterTableManagement title="Printing Substrate" apiEndpoint="/printing-substrates" fields={[{ name: 'substrate', label: 'Substrate Name', type: 'text', required: true }]} displayField="substrate" />;
      case 'lamination-substrates':
        return <MasterTableManagement title="Lamination Substrate" apiEndpoint="/lamination-substrates" fields={[{ name: 'substrate', label: 'Substrate Name', type: 'text', required: true }]} displayField="substrate" />;
      case 'priorities':
        return <MasterTableManagement title="Priority" apiEndpoint="/priorities" fields={[{ name: 'priority', label: 'Priority Level', type: 'text', required: true }]} displayField="priority" />;
      case 'defects':
        return <MasterTableManagement title="Defect" apiEndpoint="/defect-list" fields={[{ name: 'defect_name', label: 'Defect Name', type: 'text', required: true }]} displayField="defect_name" />;
      case 'bs-suppliers':
        return <MasterTableManagement title="BS Supplier" apiEndpoint="/bs-suppliers" fields={[{ name: 'supplier_name', label: 'Supplier Name', type: 'text', required: true }]} displayField="supplier_name" />;
      case 'credibility-ratings':
        return (
          <MasterTableManagement title="Credibility Rating" apiEndpoint="/credibility-ratings"
            fields={[
              { name: 'rating', label: 'Rating', type: 'text', required: true },
              { name: 'description', label: 'Description', type: 'text', required: false }
            ]}
            displayField="rating" secondaryField="description"
            reorderable
          />
        );
      case 'parent-customers':
        return <MasterTableManagement title="Parent Customer" apiEndpoint="/parent-customers" fields={[{ name: 'name', label: 'Parent Customer Name', type: 'text', required: true }]} displayField="name" />;
      case 'bs-rates':
        return <BSRateManagement />;
      case 'transports':
        return <TransportManagement />;
      case 'daily-capacity':
        return <DailyCapacitySettings />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsContent value="users"><UserManagement /></TabsContent>
          <TabsContent value="customers"><CustomerManagement /></TabsContent>
          <TabsContent value="orders"><OrderManagement /></TabsContent>

          <TabsContent value="masters">
            <div className="flex gap-6">
              {/* Master sidebar */}
              <div className="w-56 flex-shrink-0">
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  {MASTER_ITEMS.map(item => (
                    <button
                      key={item.key}
                      data-testid={`master-${item.key}`}
                      onClick={() => setActiveMaster(item.key)}
                      className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-100 last:border-b-0 transition-colors ${
                        activeMaster === item.key ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Master content */}
              <div className="flex-1">
                {renderMasterContent()}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="opening-balance">
            <div className="flex gap-6">
              <div className="w-56 flex-shrink-0">
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    data-testid="ob-bs-stock"
                    onClick={() => setActiveOBSection('bs-stock')}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      activeOBSection === 'bs-stock' ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    BS Stock
                  </button>
                </div>
              </div>
              <div className="flex-1">
                {activeOBSection === 'bs-stock' && <OpeningBalanceBSStock />}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
